import React, { useEffect, useRef, useState } from "react"
import isPermissionEnabled from "common/isPermissionEnabled"
import { getCrossModuleFilterPermissions } from "utils/crossModuleFilterPermissions"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import Filters from "components/Common/Filters"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { getAllBranchs } from "store/actions/usersAction"
import { getCountryList } from "store/actions/countriesAction"
import { getAllRegionList } from "store/actions/regionAction"
import { getAllCityList } from "store/actions/cityAction"
import {
  getLoyaltyPointsReports,
  getWalletdReports,
} from "store/actions/reportsAction"
import { getAllCustomersList } from "store/actions/customersAction"
import ReactApexChart from "react-apexcharts"
import { useTranslation } from "react-i18next"
import { getBranchCountryList } from "store/actions/commonAction"

const formatDateTime = date => {
  const pad = value => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatDateTime(startOfDay),
    max: formatDateTime(endOfDay),
  }
}

const WalletReports = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Wallet Reports ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [customerOptions, setCustomerOptions] = useState([])

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    loyaltyPointsReports,
    walletReport,
  } = useSelector(state => state.Reports)

  const { branchsList, countryList } = useSelector(state => state.Common)
  const { customersDropdownList } = useSelector(state => state.Customers)
  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )
  const { canFilterByBranch, canFilterByRegion, canFilterByCity } =
    getCrossModuleFilterPermissions(userRole, accountType, accountScope)
  const colors = ["#FF4560", "#00E396", "#FEB019", "#008FFB", "#775DD0"]

  useEffect(() => {
    const canView = isPermissionEnabled(
      userRole,
      16,
      "view_wallet_reports",
      accountType,
      accountScope
    )
    if (!canView) {
      navigateToAccessibleRoute(navigate, { userRole, accountType })
    }
  }, [userRole, accountType, accountScope, navigate])

  const [selectedFilters, setSelectedFilters] = useState({
    branchId: [],
    groupBy: "",
    countryId: [],
    regionId: [],
    cityId: [],
    customerId: [],
  })
  const [showGraph, setShowGraph] = useState(false)
  const [isShowGraphBtn, setIsShowGraphBtn] = useState(true)
  const [isShowAvailableAmount, setIsShowAvailableAmount] = useState(true)

  const [series, setSeries] = useState([])
  const [options, setOptions] = useState({
    labels: [],
    colors: colors,
    legend: { show: !1 },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
        },
      },
    },
  })

  const [dateRange, setDateRange] = useState(() => getDefaultDateRange())
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const { regionList } = useSelector(state => state.Region)
  const { cityList } = useSelector(state => state.City)
  const flatpickrRef = useRef(null)

  useEffect(() => {
    fetchLoyaltyPointsReports()
  }, [])

  useEffect(() => {
    if (canFilterByBranch) {
      dispatch(getBranchCountryList())
    }
  }, [canFilterByBranch, dispatch])

  const fetchLoyaltyPointsReports = () => {
    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      customerId: [],
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }
    dispatch(getWalletdReports(payload))
    setIsApiCalled(true)
  }

  useEffect(() => {
    if (walletReport?.length > 0) {
      const report = walletReport[0]
      const { loyaltyPointsIssued, ...otherData } = report

      const filteredEntries = Object.entries(otherData)
      const filteredValues = filteredEntries.map(([_, value]) => value)
      const updatedLabel = filteredEntries.map(([key]) =>
        t(
          key
            .split(/(?=[A-Z])/)
            .pop()
            .charAt(0)
            .toUpperCase() +
            key
              .split(/(?=[A-Z])/)
              .pop()
              .slice(1)
        )
      )

      setSeries(filteredValues)
      setOptions(prevOptions => ({
        ...prevOptions,
        labels: updatedLabel,
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: {
                  show: true,
                  label: t("Total"),
                  formatter: () => loyaltyPointsIssued,
                },
              },
            },
          },
        },
      }))
    }
  }, [walletReport, language])

  const fetchRegionList = countryId => {
    if (!canFilterByRegion) return
    dispatch(getAllRegionList(countryId))
  }

  const fetchCityList = (countryId, regionId) => {
    if (!canFilterByCity) return
    const payload = {
      countryId,
      regionId,
    }
    dispatch(getAllCityList(payload))
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  useEffect(() => {
    if (customersDropdownList.length > 0) {
      const updatedCustomerOption = customersDropdownList?.map(item => ({
        label: `${item.firstName} ${item.lastName}, ${
          item?.phoneNumber ? item?.phoneNumber : ""
        }`,
        value: item._id,
      }))
      setCustomerOptions(updatedCustomerOption)
    }
  }, [customersDropdownList])

  const filters = [
    // {
    //   name: "customerId",
    //   type: "multiSelect",
    //   options: [
    //     ...customerOptions.map(item => ({
    //       label: item.label,
    //       value: item.value,
    //     })),
    //   ],
    //   placeholder: t("Type To Search Customer"),
    // },
  ]

  // ADD REGION DROPDOWN
  if (
    canFilterByRegion &&
    Object.keys(selectedFilters.countryId).length > 0
  ) {
    filters.push({
      name: "regionId",
      type: "singleSelect",
      options: regionList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select Region"),
    })
  }

  // ADD CITY DROPDOWN
  if (
    canFilterByCity &&
    Object.keys(selectedFilters.regionId).length > 0
  ) {
    filters.push({
      name: "cityId",
      type: "singleSelect",
      options: cityList.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
      placeholder: t("Select City"),
    })
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleFilterChange = (selectedValue, filterName) => {
    const value = selectedValue.value
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
    if (filterName === "countryId") {
      // GET REGION LIST
      fetchRegionList(value)
      setSelectedFilters(prev => ({
        ...prev,
        regionId: [],
        cityId: [],
      }))
    } else if (filterName === "regionId") {
      // GET CITIES LIST
      const { countryId } = selectedFilters
      fetchCityList(countryId.value, value)
      setSelectedFilters(prev => ({
        ...prev,
        cityId: [],
      }))
    }
  }

  const getColumns = () => {
    const { groupBy } = selectedFilters
    switch (groupBy.value) {
      case "" || undefined:
        return [
          "Name",
          "Phone No.",
          "Opening Balance",
          "Top-Up Cr.",
          "Transfer Cr.",
          "Gift-Card Cr.",
          "Other Cr.",
          "Order Payment Db.",
          "Transfer Db.",
          "Other Db.",
          "Available Amount",
          "Closing Balance",
        ]
      case "order":
        return ["Invoice No", "Name", "Added Amount"]
      case "branch":
        return [
          "Branch Name",
          "Branch Ref",
          "Name",
          "Added Amount",
          "Deducted Amount On Order",
        ]
      case "city":
        return ["City Name", "Name", "Added Amount", "Deducted Amount On Order"]
      case "region":
        return [
          "Region Name",
          "Name",
          "Added Amount",
          "Deducted Amount On Order",
        ]
      case "country":
        return [
          "Country Name",
          "Name",
          "Added Amount",
          "Deducted Amount On Order",
        ]
      case "customer":
        return [
          "Customer Name",
          "Customer Email",
          "Customer PhoneNumber",
          "Name",
          "Added Amount",
          "Deducted Amount On Order",
          "Deducted Amount",
        ]
    }
  }

  const columns = getColumns()

  const renderCell = (item, column) => {
    switch (column) {
      case "Name":
        return item.name
      case "Phone No.":
        return item.phone
      case "Top-Up Cr.":
        return formatNumber(item.crWalletTopUp)
      case "Transfer Cr.":
        return formatNumber(item.crWalletTransfer)
      case "Gift-Card Cr.":
        return formatNumber(item.crWalletGiftCard)
      case "Other Cr.":
        return formatNumber(item.crWalletOther)
      case "Order Payment Db.":
        return formatNumber(item.deductedAmountOnOrder)
      case "Transfer Db.":
        return formatNumber(item.deductedAmountOnTransfer)
      case "Other Db.":
        return formatNumber(item.deductedAmountOther)
      case "Available Amount":
        return item.loyaltyPointsAvailable < 0
          ? 0
          : formatNumber(item.availableAmount)
      case "Opening Balance":
        return formatNumber(item.openingBalance)
      case "Closing Balance":
        return formatNumber(item.closingBalance)
      default:
        return item[column] || ""
    }
  }

  const formatNumber = number => {
    return Number(number)?.toFixed(2)
  }

  const handleSearch = () => {
    const { branchId, groupBy, countryId, regionId, cityId, customerId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const customerIds = customerId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    dateRange?.min
      ? setIsShowAvailableAmount(false)
      : setIsShowAvailableAmount(true)

    // HIDE GRAPH WHENEVER APPLY FILTER
    setShowGraph(false)

    if (groupBy === "" || groupBy.value === "") {
      setIsShowGraphBtn(true)
    } else {
      setIsShowGraphBtn(false)
      setShowGraph(false)
    }

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      customerId: customerIds,
      keyword: searchTerm,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }

    dispatch(getWalletdReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/wallet-reports?page=${1}`)
        }
      })
      .catch(error => {})
  }

  const handleReset = () => {
    const defaultDateRange = getDefaultDateRange()
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      customerId: [],
      dateRange: defaultDateRange,
      keyword: "",
    }
    setSearchTerm("")
    dispatch(getWalletdReports(payload))
    navigate(`/wallet-reports?page=${1}`)
    setSelectedFilters({
      branchId: [],
      groupBy: "",
      cityId: [],
      regionId: [],
      countryId: [],
      customerId: [],
    })
    setDateRange(defaultDateRange)
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate(
        [defaultDateRange.min, defaultDateRange.max],
        true
      )
    }
    setIsShowAvailableAmount(true)
  }

  const onPageChange = page => {
    const { branchId, groupBy, countryId, regionId, cityId, customerId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const customerIds = customerId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      customerId: customerIds,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }

    dispatch(getWalletdReports(payload))
    navigate(`/wallet-reports?page=${page}`)
  }

  const formatLabel = key => {
    return key
      .split(/(?=[A-Z])/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const formatValue = value => {
    return typeof value === "number"
      ? `${value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : value
  }

  const getColor = index => {
    return colors[index % colors.length]
  }

  const exportToCSV = async () => {
    const { branchId, groupBy, countryId, regionId, cityId, customerId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const customerIds = customerId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "REPORT-WALLET",
      exportType: "csv",
      customerId: customerIds,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      const fileName = hasDateRange
        ? t(`wallet_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Wallet Report Till Date")
      DownloadCSV(csvData, fileName)
    }
  }

  const handleExcelDownload = async () => {
    const { branchId, groupBy, countryId, regionId, cityId, customerId } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const customerIds = customerId?.map(item => item.value) || []
    const groupByValue = groupBy.value || ""
    const countryValue = countryId.value ? [countryId.value] : []
    const regionValue = regionId.value ? [regionId.value] : []
    const cityValue = cityId.value ? [cityId.value] : []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "REPORT-WALLET",
      exportType: "xlsx",
      customerId: customerIds,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      const fileName = hasDateRange
        ? t(`wallet_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Wallet Report Till Date")
      DownloadXLSX(xlsxData, fileName)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          {showGraph && (
            <Col xl={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">
                    {t("Wallet Report Chart")}
                  </h4>
                  <Row>
                    <Col lg={6}>
                      <div>
                        <div id="donut-chart">
                          <ReactApexChart
                            options={options}
                            series={series}
                            type="donut"
                            height={260}
                            className="apex-charts"
                          />
                        </div>
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="row text-muted">
                        {walletReport[0] &&
                          Object.entries(walletReport[0])
                            ?.filter(([key]) => key !== "loyaltyPointsIssued")
                            ?.map(([key, value], index) => (
                              <div className="col-6 mt-4" key={key}>
                                <p className="mb-2 text-truncate">
                                  <i
                                    className={`mdi mdi-circle  me-1`}
                                    style={{ color: getColor(index) }}
                                  />{" "}
                                  {t(formatLabel(key))}
                                </p>
                                <h5>{formatValue(value)}</h5>
                              </div>
                            ))}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          )}
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Wallet Reports")}
                // isAddBtn={loading ? false : isShowGraphBtn}
                addBtnText={`${showGraph ? t("Hide") : t("Show")} ${t(
                  "Graph"
                )}`}
                // onAddBtnClick={() => setShowGraph(!showGraph)}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isExportData={loading ? false : true}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  hideSearch={false}
                  searchTerm={searchTerm}
                  searchBy={t("Name, Phone Number")}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  handleReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  handleSearchChange={handleSearchChange}
                  setDateRange={setDateRange}
                  colSize={4}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : walletReport.length === 0 ? (
                    <p className="text-center my-5 py-3">
                      {t("No data found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          {columns?.map((column, index) => (
                            <th key={index}>
                              {column === "Available Amount" &&
                              !isShowAvailableAmount
                                ? null
                                : t(column)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {walletReport?.map((item, index) => (
                          <tr key={index}>
                            {columns?.map((column, colIndex) => (
                              <td key={colIndex}>
                                {column === "Available Amount" &&
                                !isShowAvailableAmount
                                  ? null
                                  : renderCell(item, column) ?? "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>

                {!loading && walletReport.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={walletReport}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}
export default WalletReports
